import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useProject } from '@/components/ProjectSelector';
import { useUser } from '@/contexts/UserContext';
import { ApiError } from '@/lib/api-request';
import { fetchBobSettings, type BobSettings } from '../settings/usage/aiUsageApi';
import { fetchCommandCenter, runReview, type CommandCenterData, type Priority, type ReviewRun, type Situation } from './api';
import { SituationCard } from './SituationCard';
import { PRIORITY_LABEL, eventText, formatDuration, formatWhen, isVisibleActivity } from './format';
import './command-center.css';

type Filter = 'all' | Priority;
const FILTERS: Filter[] = ['all', 'urgent', 'today', 'watch'];
const FILTER_LABEL: Record<Filter, string> = { all: 'All', ...PRIORITY_LABEL };
const POLL_MS = 3000;
const POLL_MAX = 40;

interface Banner { tone: 'info' | 'warn' | 'error'; text: string; retry?: boolean }

const DESKTOP_QUERY = '(min-width: 1024px)';

// Desktop shows a list and a detail panel side by side; below that each row opens in place.
function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return matches;
}

function greeting(user: { user_metadata?: Record<string, unknown> } | null): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const meta = user?.user_metadata || {};
  const raw = (meta.first_name as string) || (meta.full_name as string) || (meta.name as string) || '';
  const first = raw.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}.` : `${part}.`;
}

const RUN_LABEL: Record<ReviewRun['status'], string> = { running: 'in progress', succeeded: 'complete', partial: 'partial', failed: 'failed' };

function reviewSummary(run: ReviewRun): Banner {
  const s = run.stats || {};
  if (run.status === 'failed') return { tone: 'error', text: `The review could not be completed${run.error ? `: ${run.error}` : ''}. Your situations were not changed.`, retry: true };
  const parts = [
    s.created ? `${s.created} new` : null, s.updated ? `${s.updated} updated` : null, s.reopened ? `${s.reopened} reopened` : null, s.resolved ? `${s.resolved} resolved` : null,
  ].filter(Boolean);
  const base = parts.length > 0 ? `Review finished: ${parts.join(', ')}.` : 'Review finished: nothing changed.';
  if (run.status === 'partial') return { tone: 'warn', text: `${base} ${s.errors || 'Some'} item(s) could not be processed; run the review again to retry them.`, retry: true };
  return { tone: 'info', text: base };
}

/**
 * Command Center -- Bob's list of what needs attention. Today it has ONE kind of situation: a comment sent to a vendor that has not been answered.
 * "Reload" re-reads the list; "Run review" actually looks at the comments again (rules only, no AI). Nothing is sent, changed or notified without
 * the person approving the exact steps shown. The project comes from the global selector in the top bar.
 */
export function CommandCenterPage() {
  const { selectedProject } = useProject();
  const { user } = useUser();
  const isDesktop = useIsDesktop();
  const projectId = selectedProject && selectedProject !== 'all' ? selectedProject : undefined;

  const [data, setData] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [schedule, setSchedule] = useState<BobSettings | null>(null);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}): Promise<CommandCenterData | null> => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    if (silent) setReloading(true); else setLoading(true);
    try {
      const next = await fetchCommandCenter(projectId, abort.signal);
      if (abort.signal.aborted) return null;
      setData(next);
      setLoadError(null);
      setCooldown(next.cooldown_seconds);
      return next;
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return null;
      setLoadError(e instanceof Error ? e.message : 'The Command Center could not be loaded');
      return null;
    } finally {
      if (!abort.signal.aborted) { setLoading(false); setReloading(false); }
    }
  }, [projectId]);

  useEffect(() => { void load(); return () => controller.current?.abort(); }, [load]);
  useEffect(() => { fetchBobSettings().then(setSchedule).catch(() => setSchedule(null)); }, []);

  // the cooldown only stops repeated clicks; it counts down on screen
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // a review started elsewhere (another tab / person) is joined, not duplicated: poll until it finishes
  const polls = useRef(0);
  useEffect(() => {
    if (!data?.running || reviewing) { polls.current = 0; return undefined; }
    if (polls.current >= POLL_MAX) return undefined;
    const timer = setTimeout(() => { polls.current += 1; void load({ silent: true }); }, POLL_MS);
    return () => clearTimeout(timer);
  }, [data, reviewing, load]);

  const markChanged = (before: CommandCenterData | null, after: CommandCenterData) => {
    const seen = new Map((before?.items || []).map((i) => [i.id, i.proposal?.version || 0]));
    const flagged = new Set<string>();
    after.items.forEach((i) => { if (seen.has(i.id) && (i.proposal?.version || 0) > (seen.get(i.id) || 0)) flagged.add(i.id); });
    setChanged(flagged);
  };

  const startReview = async () => {
    setReviewing(true);
    setBanner(null);
    try {
      const before = data;
      const { run, joined } = await runReview();
      if (joined) {
        setBanner({ tone: 'info', text: 'A review is already running. The list updates when it finishes.' });
        await load({ silent: true });
        return;
      }
      const next = await load({ silent: true });
      if (next) markChanged(before, next);
      setBanner(reviewSummary(run));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'review_cooldown') {
        const wait = Number(e.details?.retry_after_seconds) || 30;
        setCooldown(wait);
        setBanner({ tone: 'info', text: e.message });
      } else {
        setBanner({ tone: 'error', text: e instanceof Error ? e.message : 'The review could not be started', retry: true });
      }
    } finally {
      setReviewing(false);
    }
  };

  const replaceItem = useCallback((updated: Situation) => {
    setData((d) => {
      if (!d) return d;
      const open = updated.status === 'open';
      return {
        ...d,
        items: open ? d.items.map((i) => (i.id === updated.id ? updated : i)) : d.items.filter((i) => i.id !== updated.id),
        handled: open ? d.handled.filter((i) => i.id !== updated.id) : [updated, ...d.handled.filter((i) => i.id !== updated.id)],
      };
    });
    setChanged((c) => { const n = new Set(c); n.delete(updated.id); return n; });
    void load({ silent: true }); // counts, activity and the last review follow the change
  }, [load]);

  const items = useMemo(() => (data ? data.items.filter((i) => filter === 'all' || i.priority === filter) : []), [data, filter]);
  const openCount = data ? data.items.length : 0;
  const selected = isDesktop ? (items.find((i) => i.id === expandedId) ?? items[0]) : undefined;
  const running = reviewing || !!data?.running;
  const onCooldown = cooldown > 0 && !running;
  const timeZone = data?.time_zone || null;

  const lastRun = data?.last_run || null;
  const statusLine = lastRun
    ? `Last review: ${lastRun.finished_at ? formatWhen(lastRun.finished_at, timeZone) : `started ${formatWhen(lastRun.started_at, timeZone)}`} · ${lastRun.trigger === 'manual' ? 'manual' : 'automatic'} · ${RUN_LABEL[lastRun.status]}`
    : 'No review has been run yet';

  const heading = !data ? ''
    : openCount === 0 ? (lastRun ? 'All clear' : 'Command Center')
      : `${openCount} ${openCount === 1 ? 'situation needs' : 'situations need'} your attention`;
  const sub = !data ? ''
    : openCount === 0 ? (lastRun ? 'No vendor comment is waiting for a reply.' : 'Run a review to look for comments that vendors have not answered.')
      : data.counts.urgent > 0 ? `${data.counts.urgent} ${data.counts.urgent === 1 ? 'is' : 'are'} urgent.` : 'None is urgent.';

  const clearBadge = (id: string) => setChanged((c) => { const n = new Set(c); n.delete(id); return n; });
  const select = (id: string) => {
    // below desktop a second tap closes the row; on desktop the detail panel always shows something
    setExpandedId((cur) => (!isDesktop && cur === id ? null : id));
    clearBadge(id);
  };
  const pickTick = (item: Situation) => {
    if (filter !== 'all' && filter !== item.priority) setFilter('all');
    select(item.id);
  };
  const handledItems = data ? data.handled : [];
  const activity = data ? data.activity.filter(isVisibleActivity) : [];

  const emptyQueue = !data ? null : openCount > 0 ? (
    <><p className="bcc-empty-title">Nothing under {FILTER_LABEL[filter]} right now.</p><p className="bcc-empty-text">Switch to All to see the other situations.</p></>
  ) : lastRun ? (
    <><p className="bcc-empty-title">No pending items.</p><p className="bcc-empty-text">Every comment sent to a vendor has been answered or is still within {data.settings.followup_after_hours} hours. Last review {lastRun.finished_at ? formatWhen(lastRun.finished_at, timeZone) : 'is still running'}.</p></>
  ) : (
    <><p className="bcc-empty-title">Bob hasn&apos;t reviewed yet.</p><p className="bcc-empty-text">Run a review to look for comments sent to vendors that have had no reply for {data.settings.followup_after_hours} hours or more.</p></>
  );

  return (
    <div className="bcc">
      <div className="bcc-wrap">
        <section className="bcc-hero" aria-label="Review">
          <div className="bcc-hero-top">
            <div className="bcc-hero-text">
              <p className="bcc-bob-status">
                <span className={`bcc-pulse${running ? ' is-running' : ''}`} aria-hidden="true" />
                <span>{running ? 'Reviewing comments…' : statusLine}</span>
              </p>
              <h1 className="bcc-hello">{greeting(user)}</h1>
              {data && (
                <p className="bcc-sub" aria-live="polite">
                  <span role="heading" aria-level={2}>{heading}</span>
                  {sub && <>{'. '}<span>{sub}</span></>}
                </p>
              )}
            </div>
            <div className="bcc-review-actions">
              <button type="button" className="bcc-icon-btn" aria-label="Reload list" title="Reload the list (does not run a review)" disabled={loading || reloading} onClick={() => void load({ silent: true })}>
                <RefreshCw className={`h-4 w-4${reloading ? ' bcc-spin' : ''}`} aria-hidden />
              </button>
              <button type="button" className="bcc-act bcc-act-primary" disabled={running || onCooldown} title="Looks for comments to vendors that have had no reply. Rules only: this does not use AI." onClick={() => void startReview()}>
                {running ? 'Review in progress…' : onCooldown ? `Run review (in ${cooldown}s)` : 'Run review'}
              </button>
            </div>
          </div>

          {data && data.items.length > 0 && (
            <div className="bcc-ticks" role="group" aria-label="Open situations by priority">
              {data.items.map((item, i) => (
                <button
                  key={item.id} type="button" aria-label={item.title}
                  className={`bcc-tick bcc-prio-${item.priority}${(selected?.id ?? expandedId) === item.id ? ' is-active' : ''}`}
                  style={{ animationDelay: `${0.12 + i * 0.05}s` }}
                  onClick={() => pickTick(item)}
                />
              ))}
            </div>
          )}

          {data && (
            <div className="bcc-filters" role="tablist" aria-label="Filter by priority">
              {FILTERS.map((f) => {
                const count = f === 'all' ? openCount : data.counts[f];
                return (
                  <button key={f} type="button" role="tab" aria-selected={filter === f} className={`bcc-filter${filter === f ? ' is-on' : ''}`} onClick={() => setFilter(f)}>
                    {FILTER_LABEL[f]} <span className="bcc-filter-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="bcc-meta">
            <span>{projectId ? 'Showing the project selected in the top bar' : 'Showing all projects'}</span>
            {schedule && !schedule.scheduler.running && <span>Automatic daily reviews are not running yet</span>}
          </div>
          {banner && (
            <div className={`bcc-banner bcc-banner-${banner.tone}`} role={banner.tone === 'error' ? 'alert' : 'status'}>
              <span>{banner.text}</span>
              {banner.retry && <button type="button" onClick={() => void startReview()} disabled={running}>Run again</button>}
            </div>
          )}
          {lastRun && lastRun.status === 'failed' && !banner && (
            <div className="bcc-banner bcc-banner-error" role="alert"><span>The last review failed{lastRun.error ? `: ${lastRun.error}` : ''}. The list below is from before it.</span></div>
          )}
        </section>

        {loadError && (
          <div className="bcc-banner bcc-banner-error" role="alert">
            <span>{loadError}</span>
            <button type="button" onClick={() => void load()}>Retry</button>
          </div>
        )}

        {loading && !data && (
          <div className="bcc-panel" aria-busy="true" aria-label="Loading" data-testid="cc-loading">
            <div className="bcc-skeleton" /><div className="bcc-skeleton" style={{ marginTop: '0.6rem' }} />
          </div>
        )}

        {data && (
          <>
            <section className="bcc-main" aria-label="Situations">
              <div className="bcc-queue bcc-panel">
                {items.length > 0 ? (
                  isDesktop ? (
                    <ul className="bcc-list" aria-label="Situations">
                      {items.map((item) => (
                        <li key={item.id} className={`bcc-row${selected?.id === item.id ? ' is-sel' : ''}`} data-testid="situation-row" data-item-id={item.id}>
                          <button type="button" className="bcc-row-btn" aria-current={selected?.id === item.id} onClick={() => select(item.id)}>
                            <span className={`bcc-prio bcc-prio-${item.priority}`} role="img" aria-label={PRIORITY_LABEL[item.priority]} />
                            <span className="bcc-row-body">
                              <span className="bcc-row-project">{item.project_name || item.entity.label}</span>
                              <span className="bcc-row-title">{item.title}{changed.has(item.id) && selected?.id !== item.id && <span className="bcc-new">Updated</span>}</span>
                            </span>
                            <span className="bcc-row-age">{formatDuration(item.hours_waiting)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="bcc-list" aria-label="Situations">
                      {items.map((item) => (
                        <SituationCard
                          key={item.id} item={item} timeZone={timeZone} expanded={expandedId === item.id} isNew={changed.has(item.id) && expandedId !== item.id}
                          onToggle={() => select(item.id)} onChanged={replaceItem}
                        />
                      ))}
                    </ul>
                  )
                ) : (
                  <div className="bcc-empty" data-testid="cc-empty">
                    {emptyQueue}
                    {openCount === 0 && <button type="button" className="bcc-act bcc-act-soft" disabled={running || onCooldown} onClick={() => void startReview()}>{running ? 'Review in progress…' : 'Run review'}</button>}
                  </div>
                )}
              </div>

              {isDesktop && (
                <div className="bcc-stage bcc-panel">
                  {selected ? (
                    <div className="bcc-stage-in" key={selected.id}>
                      <SituationCard item={selected} stage timeZone={timeZone} expanded isNew={changed.has(selected.id)} onToggle={() => undefined} onChanged={replaceItem} />
                    </div>
                  ) : (
                    <div className="bcc-empty">
                      <p className="bcc-empty-title">No pending items.</p>
                      <p className="bcc-empty-text">When Bob finds something that needs you, the evidence and the steps he suggests appear here.</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            <div className="bcc-lower">
              <section className="bcc-panel" aria-label="Handled recently">
                <h2 className="bcc-section-title">Handled in the last 14 days</h2>
                {handledItems.length > 0 ? (
                  <ul className="bcc-list">
                    {handledItems.map((item) => (
                      <SituationCard key={item.id} item={item} handled timeZone={timeZone} expanded={expandedId === item.id} onToggle={() => setExpandedId((cur) => (cur === item.id ? null : item.id))} onChanged={replaceItem} />
                    ))}
                  </ul>
                ) : <p className="bcc-empty-text">Nothing has been handled in the last 14 days.</p>}
              </section>

              <section className="bcc-panel" aria-label="Recent activity">
                <h2 className="bcc-section-title">Recent activity</h2>
                {activity.length > 0 ? (
                  <ul className="bcc-log">
                    {activity.map((entry) => (
                      <li key={entry.id}><time>{formatWhen(entry.at, timeZone)}</time>{eventText(entry, entry.entity_label)}</li>
                    ))}
                  </ul>
                ) : <p className="bcc-empty-text">No recent activity.</p>}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CommandCenterPage;
